import { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "@/i18n/i18n";

interface Props {
  children: ReactNode;
  /** Optional fallback UI. Defaults to built-in error card. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches any unhandled JS error in the child component tree and renders
 * a recoverable error card instead of crashing the entire application.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production, send to Sentry / error tracker
    console.error("[ErrorBoundary] Uncaught error:", error, info.componentStack);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="error-boundary">
          <div className="error-boundary-card">
            <div className="error-boundary-icon">⚠️</div>
            <h2>{i18n.t("common.errorOccurred")}</h2>
            <p className="error-boundary-message">
              {this.state.error?.message || i18n.t("common.unexpectedError")}
            </p>
            <div className="error-boundary-actions">
              <button className="btn btn-primary" onClick={this.handleRetry}>
                {i18n.t("common.retry")}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => window.location.reload()}
              >
                {i18n.t("common.reload")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
